/**
 * The auth seam the rest of the app reads. Synchronous on purpose: axios
 * interceptors, RxDB replication and the WebSocket manager need a token without
 * awaiting, and route guards run in `beforeLoad`.
 *
 * Replaces oidcTokenUtils.ts (Cognito / oidc-client-ts).
 */

import {
  clearStoredAccessToken,
  clearStoredUser,
  getSessionToken,
  getStoredAccessToken,
  getStoredUser,
  setSessionToken,
  setStoredUser,
} from './authStorage'
import { decodeAccessToken, hasUsableAccessToken, refreshAccessToken } from './tokenRefresh'
import type { AuthUser } from './authStorage'
import type { RefreshOutcome } from './tokenRefresh'
import type { SignedInSession } from './authApi'

export type { AuthUser } from './authStorage'

/**
 * The current API JWT, or '' when there is none. May be expired: callers send
 * it anyway and the 401 path refreshes. An expired token also kicks off a
 * background refresh so the next call has a fresh one.
 */
export const getAccessToken = (): string => {
  const token = getStoredAccessToken()
  if (token && getSessionToken() && !hasUsableAccessToken()) {
    void refreshAccessToken()
  }
  return token ?? ''
}

/**
 * True when this device holds a Better Auth session. Deliberately does not
 * require a live JWT or the network: the app is offline-first, and a session
 * the server has revoked is detected (and logged out) on the next refresh.
 */
export const isAuthenticated = (): boolean => getSessionToken() !== null

/**
 * The signed-in user. Falls back to the JWT's claims (sub, email) when the
 * profile hasn't been stored yet. Replaces `getOidcUser()`.
 */
export const getAuthUser = (): AuthUser | null => {
  if (!getSessionToken()) return null
  const stored = getStoredUser()
  if (stored) return stored
  const token = getStoredAccessToken()
  const claims = token ? decodeAccessToken(token) : null
  if (!claims?.sub || !claims.email) return null
  return { id: claims.sub, email: claims.email, name: '', emailVerified: true, image: null }
}

/**
 * Store a session the server just created (sign-in, or verifying the sign-up
 * code) and mint its first JWT, so the API calls that follow are authorised.
 */
export async function establishSession(session: SignedInSession): Promise<RefreshOutcome> {
  // Never let a previous session's JWT or profile outlive it.
  clearStoredAccessToken()
  setSessionToken(session.sessionToken)
  if (session.user) setStoredUser(session.user)
  else clearStoredUser()
  const outcome = await refreshAccessToken()
  // A refresh for the previous session was still in flight and got discarded.
  return outcome === 'superseded' ? refreshAccessToken() : outcome
}
